import { useCallback } from 'react';

import { useAppStore, getAppStore, setAppStore, type IAppStore } from '@/store';
import { useBrowserHistory } from '@/hooks/useBrowserHistory';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import closeSearch from '@/utils/search/closeSearch';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import { Screen } from '@/types';

interface UseAppNavigationOptions {
  onBackFromSettings: () => void;
  onBackFromProfile: () => void;
  onBackFromCredits: () => void;
}

const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
});

/**
 * Wires the browser back-button (Android WebView + browser) to the in-app
 * navigation. Each back handler is a single concern: analytics + state.
 * The lightbox lives in the store (`AppLightbox`), so back closes it directly.
 */
const useAppNavigation = ({
  onBackFromSettings,
  onBackFromProfile,
  onBackFromCredits,
}: UseAppNavigationOptions) => {
  const { screen, setScreen } = useAppStore(selector);

  const onBackToLibrary = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    resetCanvasLoadState();
    setScreen(Screen.Library);
  }, [setScreen]);

  const onBackFromFiles = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    setScreen(Screen.Library);
  }, [setScreen]);

  const onBackFromLightbox = useCallback(
    () => getAppStore().closeLightbox(),
    []
  );

  const onBackFromSeeAll = useCallback(() => {
    setAppStore({ seeAllCategorySlug: null });
  }, []);

  // Shared with the in-app back arrow, so either path mutes and reports once.
  const onBackFromSearch = useCallback(() => closeSearch(), []);

  const onCloseApp = useCallback(() => {
    try {
      closeMiniapp();
    } catch {
      // SDK may not be available
    }
  }, []);

  useBrowserHistory({
    screen,
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onBackFromProfile,
    onBackFromCredits,
    onBackFromSearch,
    onBackFromSeeAll,
    onCloseApp,
  });
};

export default useAppNavigation;
